import { createContext, FC, ReactNode, useMemo, useState } from 'react'
import {
  AnimationsEnum,
  animationSettings,
  defaultAnimationDelay,
  defaultAnimationStartFrame,
  defaultNoAnimationOption,
  ModelNamesEnum,
  voicesMap,
  AnimationsInterruptArray,
  AnimationsListeningArray,
  AnimationsThinkingArray
} from '../constants/talkingHead'
import { useControls } from 'leva'
import { speechStartDelayInMs } from '../constants/speech'
import { MathUtils } from 'three'
import { getRandomAnimation } from '../utils/getAnimation'

type VisemeType = {
  id: string,
  visemeId: number,
  offset: number,
  animation?: string
}
export type TalkingHeadContextType = {
  modelName: {
    modelName: ModelNamesEnum,
  },
  animation: {
    animation: AnimationsEnum,
    setAnimation: (newAnimation: AnimationsEnum) => void,
    backToIdle: () => void,
    interrupt: () => void,
    think: () => void,
    stopThinking: () => void,
    listen: () => void,
    rotateIdle: () => void,
    rotateTalk: () => void,
    fadeInDelay: number,
    fadeOutDelay: number,
    startFrame: number
  },
  activeAnimation: {
    activeAnimation: string,
    updateActiveAnimation: (newAnimation: string) => void,
  }
  animationCicle: {
    cicleCount: number,
    updateCicleCount: (newCicleCount: number | ((prev: number) => number)) => void,
  }
  modelAnimations: {
    modelAnimations: string[],
    updateModelAnimations: (animations: string[]) => void,
  },
  voice: string,
  visemes: {
    visemes: VisemeType[],
    addViseme: (viseme: VisemeType) => void,
    removeViseme: (id: string) => void,
    clearVisemes: () => void
  },
  talking: {
    isTalking: boolean,
    setTalking: (newTalkingState: boolean, settings?: { wasInterrupted?: boolean, actualStartTime?: number }) => void,
    talkStartedAt?: number
  },
  speech:{
    isTextToSpeechMuted: boolean,
    setIsTextToSpeechMuted: (newState: boolean | ((prev: boolean) => boolean)) => void
  }
}

export const TalkingHeadContext = createContext<TalkingHeadContextType | null>(null)

const TalkingHeadProvider: FC<{children: ReactNode}> = ({ children }) => {
  // Leva controls
  const [
    { modelName, animation,
      idleStartedFadeInDelay, idleStartedFadeOutDelay, idleStartedStartFrame,
      thinkStartedFadeInDelay, thinkStartedFadeOutDelay, thinkStartedStartFrame,
      thinkFadeInDelay, thinkFadeOutDelay, thinkStartFrame,
      talkStartedFadeInDelay, talkStartedFadeOutDelay, talkStartedStartFrame,
      talkIdleFadeInDelay, talkIdleFadeOutDelay, talkIdleStartFrame
    },
    set
  ] = useControls('Talking Head', () => {
    return {
      modelName: {
        value: ModelNamesEnum.Leobot,
        options: Object.values(ModelNamesEnum)
      },
      animation: {
        value: AnimationsEnum.Wave,
        options: Object.values(AnimationsEnum)
      },
      idleStartedFadeInDelay: {
        value: 0
      },
      idleStartedFadeOutDelay: {
        value: 0
      },
      idleStartedStartFrame: {
        value: 0
      },
      thinkStartedFadeInDelay: {
        value: 0
      },
      thinkStartedFadeOutDelay: {
        value: 0
      },
      thinkStartedStartFrame: {
        value: 0
      },
      thinkFadeInDelay: {
        value: 0
      },
      thinkFadeOutDelay: {
        value: 0
      },
      thinkStartFrame: {
        value: 0
      },
      talkStartedFadeInDelay: {
        value: 0
      },
      talkStartedFadeOutDelay: {
        value: 0
      },
      talkStartedStartFrame: {
        value: 0
      },
      talkIdleFadeInDelay: {
        value: 0
      },
      talkIdleFadeOutDelay: {
        value: 0
      },
      talkIdleStartFrame: {
        value: 0
      },
    }
  })

  const [modelAnimations, setModelAnimations] = useState<string[]>([])
  const [activeAnimation, setActiveAnimation] = useState<string>(defaultNoAnimationOption)
  const [cicleCount, setCicleCount] = useState<number>(0)
  const [visemes, setVisemes] = useState<VisemeType[]>([])
  const [isTextToSpeechMuted, setIsTextToSpeechMuted] = useState<boolean>(true)

  const updateCicleCount = (newCicleCount: number | ((prev: number) => number)) =>
    setCicleCount(newCicleCount);
  
  const updateModelAnimations = (modelAnimations: string[]) => setModelAnimations(modelAnimations || [defaultNoAnimationOption])
  const updateActiveAnimation = (newAnimation: string) => setActiveAnimation(newAnimation)
  const addViseme = (viseme: VisemeType) => setVisemes((prevVisemes) => [...prevVisemes, viseme])
  const removeViseme = (id: string) => {
    setVisemes((prevVisemes) => prevVisemes.filter((viseme) => viseme.id != id))
  }
  const clearVisemes = () => setVisemes([])

  const [talkStartedAt, setTalkStartedAt] = useState<number>()

  const isTalking = !!talkStartedAt && visemes.length > 0
  const setTalking = (newTalkingState: boolean, settings?: { wasInterrupted?: boolean, actualStartTime?: number }) => {

    if (!newTalkingState) {
      return setTalkStartedAt((prevTalkStartedAt) => {
        clearVisemes();
        settings?.wasInterrupted ? interrupt() : backToIdle();
        return 0;
      });
    }
  
    setTalkStartedAt(() => {
      return settings?.actualStartTime || (Date.now() + speechStartDelayInMs);
    });
  }
  // const modelPath = `${modelsPath}/${modelName}`
  // const modelFilePath = `${modelPath}/${modelName}.glb`

  let fadeInDelay = animationSettings[animation]?.fadeInDelay || defaultAnimationDelay
  let fadeOutDelay = animationSettings[animation]?.fadeOutDelay || defaultAnimationDelay
  let startFrame = animationSettings[animation]?.startFrame || defaultAnimationStartFrame

  const setAnimation = (newAnimation: AnimationsEnum) => set({ animation: newAnimation })
  const backToIdle = () => setAnimation(AnimationsEnum.Idle)
  const interrupt = () => {
    const randomAnimation = getRandomAnimation(AnimationsInterruptArray)
    setAnimation(randomAnimation)
  }
  const think = () => {
    const randomAnimation = getRandomAnimation(AnimationsThinkingArray)
    setAnimation(randomAnimation)
  }
  const stopThinking = () => {
    if (animation === AnimationsEnum.ThinkStarted) {
      return setAnimation(isTalking ? AnimationsEnum.Talk2 : AnimationsEnum.Idle3)
    }

    setAnimation(isTalking ? AnimationsEnum.TalkStarted : AnimationsEnum.Idle2)
  }
  const listen = () => {
    const randomAnimation = getRandomAnimation(AnimationsListeningArray)
    setAnimation(randomAnimation)
  }
  const rotateIdle = () => {
    const animationChance = MathUtils.randInt(0, 100)
    let randomAnimation

    if (animationChance < 70)
      randomAnimation = AnimationsEnum.Idle3
    else if (animationChance < 85)
      randomAnimation = AnimationsEnum.Idle2
    else
      randomAnimation = AnimationsEnum.Idle

    if (randomAnimation != animation) setAnimation(randomAnimation)
  }
  const rotateTalk = () => {
    const animationChance = MathUtils.randInt(0, 100)
    let randomAnimation

    if (animationChance < 60)
      // randomAnimation = AnimationsEnum.TalkIdle
      randomAnimation = AnimationsEnum.TalkStarted
    else if (animationChance < 70)
      // randomAnimation = AnimationsEnum.Talk
      randomAnimation = AnimationsEnum.TalkStarted
    else if (animationChance < 80)
      randomAnimation = AnimationsEnum.Talk2
    else
      randomAnimation = AnimationsEnum.Talk3

    if (randomAnimation != animation) setAnimation(randomAnimation)
  }

  const contextValue = useMemo(() => ({
    modelName: { modelName },
    animation: {
      animation,
      setAnimation,
      backToIdle,
      interrupt,
      think,
      stopThinking,
      listen,
      rotateIdle,
      rotateTalk,
      fadeInDelay,
      fadeOutDelay,
      startFrame
    },
    activeAnimation: {
      activeAnimation,
      updateActiveAnimation
    },
    animationCicle: {
      cicleCount,
      updateCicleCount
    },
    modelAnimations: {
      modelAnimations,
      updateModelAnimations,
    },
    voice: voicesMap[modelName],
    visemes: { visemes, addViseme, clearVisemes, removeViseme },
    talking: { isTalking, setTalking, talkStartedAt },
    speech:{ isTextToSpeechMuted, setIsTextToSpeechMuted}
  }), [
    modelName,
    animation,
    setAnimation,
    backToIdle,
    interrupt,
    think,
    stopThinking,
    listen,
    rotateIdle,
    rotateTalk,
    fadeInDelay,
    fadeOutDelay,
    startFrame,
    activeAnimation,
    updateActiveAnimation,
    cicleCount,
    updateCicleCount,
    modelAnimations,
    updateModelAnimations,
    voicesMap,
    visemes,
    addViseme,
    clearVisemes,
    removeViseme,
    isTalking,
    setTalking,
    talkStartedAt,
    isTextToSpeechMuted,
    setIsTextToSpeechMuted
  ]);

  return (
    <TalkingHeadContext.Provider value={contextValue}>
      {children}
    </TalkingHeadContext.Provider>
  )
}
export default TalkingHeadProvider
